import Cookies from 'js-cookie';

import { callApi } from '../../apiCaller';
import { signOut } from '../../helpers/auth';
import {
     addTimezoneLocal,
     asyncLocalStorage,
     checkLicenseeUserAndCart,
     clearRelatedLocalCartLocalStorage,
     clearSomeLocalStorage,
     removeURLParameter,
     toastError,
     tryParseJSON,
} from '../../utils';
import {
     API_AUTH_SIGN_IN,
     API_GUEST_SUBSCRIBER_UPDATE_CART,
     API_SUBSCRIBER_ORGANIZATION_USER_UPDATE,
     COOKIE_NAMES,
     LOCAL_CART,
     LOCAL_COUPON,
     LOCAL_COUPON_VOUCHERS,
     LOCAL_DIRECT_PATH,
     LOCAL_LICENSEE,
     LOCAL_LOGIN_AS_SESSION,
     LOCAL_LOGIN_AS_UNDER_USER,
     LOCAL_ORG_USER,
     MY_LEARNING_PATH,
     SUBSCRIBER_PATH,
     SUBSCRIBER_USER_ROLES,
     SYSTEM_ADMIN_PATH,
     SYSTEM_ADMIN_ROUTES,
     USER_ROLES,
} from '../../constants';

import { setShow, setShowLoginAs } from '../../actions/general';
import { fetchCartRequest } from '../CMS/Guest/Cart/actions';
import { removeDataRelatedToCart } from '../CMS/Guest/Checkout/action';
import { setSubscriberUser } from '../CMS/Subscriber/GlobalStates/CurrentUser/actions';
import { setSubscriberLicenseeId } from '../CMS/Subscriber/Licensees/actions';
import { setManagedOrganizations } from '../CMS/Organizations/ManagedOrganizations/actions';

export const API_SYSTEM_ADMIN_USER_LOGIN_AS = 'auth/login-as';
export const API_SYSTEM_ADMIN_USER_LOGIN_AS_SESSION = 'auth/login-as-session';
export const API_SYSTEM_ADMIN_USER_LOGIN_AS_SESSION_DETAIL = 'auth/:id/login-as-session';

export const API_SUBSCRIBER_UNSUBSCRIBE_EMAIL = 'sendgrid/unsubscribe-email';

// Login As
const loginAsSessionRequest = (values) => () => {
     callApi(API_SYSTEM_ADMIN_USER_LOGIN_AS_SESSION, 'POST', values).then((response) => {
          if (response.status !== 200) {
               toastError(response);
               return;
          }

          asyncLocalStorage.setItem(LOCAL_LOGIN_AS_SESSION, JSON.stringify(response.data)).then(() => {
               // Refreshing a page when navigate go to page
               window.location.reload(true);
          });
     });
};

export const loginAsRequest = (values, nextDispatch) => async (dispatch, getState) => {
     const currentUser = getState().subscriber.user;

     const { orgId, role, type, navigate } = nextDispatch;
     const localLoginAsSession = tryParseJSON(localStorage.getItem(LOCAL_LOGIN_AS_SESSION));
     dispatch(setShowLoginAs(true));

     return await callApi(API_SYSTEM_ADMIN_USER_LOGIN_AS, 'POST', values).then((response) => {
          if (response.status !== 200) {
               toastError(response);
               return;
          }

          const isOrganization = type === SUBSCRIBER_USER_ROLES.ORGANIZATION;
          const userResponse = response.data;

          localStorage.setItem(LOCAL_LOGIN_AS_UNDER_USER, JSON.stringify({ role }));

          dispatch(setSubscriberUser(userResponse.user));

          if (role === USER_ROLES.SYSTEM_ADMIN || role === USER_ROLES.SYSTEM_ADMIN_LOWER) {
               if (isOrganization) {
                    callApi(API_SUBSCRIBER_ORGANIZATION_USER_UPDATE.replace(':organizationId', orgId), 'GET').then((response) => {
                         if (response.status !== 200) {
                              toastError(response);
                              return;
                         }

                         const orgUser = response.data;
                         addTimezoneLocal(orgUser.organization.timezone);
                         localStorage.setItem(LOCAL_ORG_USER, JSON.stringify(orgUser));
                         navigate && navigate(SUBSCRIBER_PATH.ORGANIZATIONS_DASHBOARD);
                    });
               }

               const sessionPayload = {
                    userId: currentUser.id,
                    token: userResponse.token,
               };

               dispatch(loginAsSessionRequest(sessionPayload));
               dispatch(setManagedOrganizations(null));
               return userResponse;
          }

          if (!localLoginAsSession || !navigate) return userResponse;

          dispatch(deleteLoginAsSessionRequest(localLoginAsSession.id));
          dispatch(setShowLoginAs(false));

          if (isOrganization) {
               navigate(SYSTEM_ADMIN_PATH.ORGANIZATIONS, { replace: true });
          } else {
               navigate(SYSTEM_ADMIN_PATH.USERS, { replace: true });
          }

          return userResponse;
     });
};

export const fetchLoginAsSessionDetailRequest = async (id) => {
     const loginAsSessionRes = await callApi(API_SYSTEM_ADMIN_USER_LOGIN_AS_SESSION_DETAIL.replace(':id', id), 'GET');

     if (loginAsSessionRes.status !== 200) {
          toastError(loginAsSessionRes);
          signOut();
          return;
     }

     return loginAsSessionRes.data.user;
};

export const deleteLoginAsSessionRequest = (id) => () => {
     callApi(API_SYSTEM_ADMIN_USER_LOGIN_AS_SESSION_DETAIL.replace(':id', id), 'DELETE').then((response) => {
          if (response.status !== 200) {
               toastError(response);
               signOut();
               return;
          }

          clearRelatedLocalCartLocalStorage();
          clearSomeLocalStorage();
     });
};

export const loginRequest = (loginPayload, nextDispatch) => (dispatch, getState) => {
     const afterLogin = getState().general.afterLogin;

     const { licensees, products, setSubmitting, navigate } = nextDispatch;
     const orgUserLocalStorage = tryParseJSON(localStorage.getItem(LOCAL_ORG_USER));

     callApi(API_AUTH_SIGN_IN, 'POST', loginPayload).then((response) => {
          setSubmitting(false);

          if (response.status !== 200) {
               toastError(response);
               return;
          }

          const user = response.data;
          const userId = user.id;
          const cartLocalStorage = tryParseJSON(localStorage.getItem(LOCAL_CART));

          Cookies.set(COOKIE_NAMES.TP_IS_LOGGED_IN, 1);
          dispatch(setSubscriberUser(user));

          const licenseeId = licensees?.find((item) => item.country.includes(user.country))?.id || '';
          dispatch(setSubscriberLicenseeId(licenseeId));

          if (!cartLocalStorage || (cartLocalStorage.userId && cartLocalStorage.userId !== userId)) {
               localStorage.removeItem(LOCAL_CART);
               localStorage.removeItem(LOCAL_COUPON);
               localStorage.removeItem(LOCAL_COUPON_VOUCHERS);
          }

          if (cartLocalStorage && !cartLocalStorage.userId) {
               const isRemoveCart = user.country ? checkLicenseeUserAndCart(cartLocalStorage, licenseeId, products, true) : false;

               if (!isRemoveCart) {
                    const cartPayload = {
                         id: cartLocalStorage.id,
                         userId,
                    };

                    callApi(API_GUEST_SUBSCRIBER_UPDATE_CART, 'PUT', cartPayload).then((response) => {
                         if (response.status !== 200) {
                              dispatch(removeDataRelatedToCart());
                              return;
                         }

                         response.data && dispatch(fetchCartRequest(response.data.id));
                    });
               }
          }

          localStorage.removeItem(LOCAL_LICENSEE);
          dispatch(setShow(afterLogin || null));

          const localDirectPath = localStorage.getItem(LOCAL_DIRECT_PATH) || '';

          if (user.role === USER_ROLES.SUBSCRIBER) {
               const userOrganizations = user.organizations || [];
               const isOrgUser = orgUserLocalStorage && userOrganizations.length;
               const directPath = localDirectPath || MY_LEARNING_PATH.DASHBOARD;
               const newDirectPath = removeURLParameter(directPath, 'organizationId');

               if (!isOrgUser) {
                    asyncLocalStorage.removeItem(LOCAL_ORG_USER).then(() => {
                         const directPathFinal = removeURLParameter(newDirectPath, 'email');
                         navigate(directPathFinal, { replace: true });
                    });
                    return;
               }

               const matchOrg = userOrganizations.some((userOrg) => userOrg.id === orgUserLocalStorage.organizationId);
               const directPathOrg = directPath.includes('organizationId') ? newDirectPath : MY_LEARNING_PATH.DASHBOARD;

               if (!matchOrg) {
                    asyncLocalStorage.removeItem(LOCAL_ORG_USER).then(() => {
                         navigate(directPathOrg, { replace: true });
                    });
               } else {
                    navigate(directPathOrg, { replace: true });
               }

               return;
          }

          const adminNavigate = localDirectPath.includes(SYSTEM_ADMIN_ROUTES) ? localDirectPath : SYSTEM_ADMIN_PATH.DASHBOARD;
          navigate(adminNavigate, { replace: true });
     });
};

export const unsubscribeEmailRequest = (dataEncode) => async () => {
     return await callApi(API_SUBSCRIBER_UNSUBSCRIBE_EMAIL, 'POST', { dataEncode }).then((response) => {
          if (response.status !== 200) {
               toastError(response);
               return;
          }

          return response.data;
     });
};
